import React from "react";
import { motion, type Easing } from "framer-motion";
import { Helmet } from "react-helmet-async"; // Assuming react-helmet-async is installed

import {
  FaSearch,
  FaChartLine,
  FaRocket,
  FaShieldAlt,
  FaLightbulb,
  FaDollarSign,
  FaHeadset,
  FaQuestionCircle,
  FaUsers,
  FaCode,
  FaMobileAlt,
  FaLink, // For link building
  FaPencilAlt, // For content creation
  FaMapMarkedAlt, // For local SEO
  FaBullhorn, // For digital marketing/strategy
} from "react-icons/fa"; // Added more icons for new sections
import { Link } from "react-router-dom";

// Animation variants (reusing from previous files for consistency)
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeProcessStep = (index: number) => ({
  hidden: { opacity: 0, x: index % 2 === 0 ? -50 : 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0, 0, 0.58, 1] as Easing,
    },
  },
});

const SearchEngineOptimizationPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <Helmet>
        <title>Top SEO Services in Noida | Boost Your Rankings | Nadezhda Venture</title>
        <meta
          name="description"
          content="Leading SEO company in Noida offering comprehensive Search Engine Optimization services to increase organic traffic, leads, and conversions. Get higher rankings on Google."
        />
        <meta
          name="keywords"
          content="SEO services Noida, SEO company Noida, Search Engine Optimization, local SEO, organic traffic, Google ranking, digital marketing, website optimization"
        />
        <meta name="author" content="Nadezhda Venture" />

        {/* Open Graph for social sharing */}
        <meta property="og:title" content="Top SEO Services in Noida | Nadezhda Venture" />
        <meta property="og:description" content="Boost your online visibility and drive more organic traffic with our expert SEO strategies in Noida." />
        <meta property="og:image" content="https://www.searchenginejournal.com/wp-content/uploads/2021/04/the-complete-guide-to-seo-6081ab7912448.png" /> {/* SEO related image */}
        <meta property="og:url" content="https://yourdomain.com/seo-services-noida" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* SECTION 1 - Hero Section */}
      <section className="h-[400px] bg-gradient-to-br from-gray-900 via-black to-gray-950 relative overflow-hidden flex items-center justify-center">
        {/* Background Image */}
        <img
          src="https://neilpatel.com/wp-content/uploads/2021/09/seo-for-developers.png" // SEO specific image
          className="absolute inset-0 w-full h-full object-cover opacity-50 z-0"
          alt="SEO Background"
        />

        {/* Overlay with blur for glass-like effect */}
        <div className="absolute inset-0 opacity-30 z-0" />

        <motion.div
          className="relative z-10 max-w-4xl px-6 py-32 md:py-48 text-center md:text-left"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInLeft}
        >
          <h1 className="text-3xl md:text-4xl font-extrabold text-orange-500 mb-4">
            Dominate Search Rankings with Expert SEO
          </h1>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
            Unlock your website's full potential with our comprehensive Search Engine Optimization services in Noida. Drive organic traffic, increase visibility, and achieve sustainable growth.
          </p>
          <div className="mt-8">
           <Link to="/contact">
  <button className="bg-orange-700 hover:bg-orange-600 px-6 py-3 rounded-lg font-semibold transition text-white">
            Get Started Now
  </button>
</Link>
          </div>
        </motion.div>
      </section>

      {/* SECTION 2 - Why SEO is Crucial */}
      <section className="bg-white  text-white py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-500">
            Why Search Engine Optimization is Crucial for Your Business?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Increased Organic Traffic",
                desc: "Higher search engine rankings mean more free, qualified visitors to your website, boosting potential leads and sales.",
                icon: <FaChartLine size={50} className="text-orange-400 mb-4 mx-auto" />,
              },
              {
                title: "Enhanced Brand Visibility",
                desc: "Being at the top of search results establishes your brand as an authority and increases its credibility and recognition.",
                icon: <FaBullhorn size={50} className="text-blue-400 mb-4 mx-auto" />,
              },
              {
                title: "Cost-Effective Marketing",
                desc: "Unlike paid ads, organic traffic generated through SEO provides a continuous flow of visitors without ongoing per-click costs.",
                icon: <FaDollarSign size={50} className="text-green-400 mb-4 mx-auto" />,
              },
              {
                title: "Improved User Experience",
                desc: "SEO practices often involve optimizing website speed, mobile-friendliness, and content quality, leading to a better user experience.",
                icon: <FaMobileAlt size={50} className="text-purple-400 mb-4 mx-auto" />,
              },
              {
                title: "Higher Conversion Rates",
                desc: "Visitors from organic search are often actively looking for information, products, or services, making them more likely to convert.",
                icon: <FaRocket size={50} className="text-red-400 mb-4 mx-auto" />,
              },
              {
                title: "Competitive Advantage",
                desc: "Outrank your competitors on search engines and capture a larger share of the online market in your industry.",
                icon: <FaShieldAlt size={50} className="text-white mb-4 mx-auto" />,
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="bg-blue-950 p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-orange-600"
              >
                {card.icon}
                <h3 className="text-xl font-semibold mb-3 mt-2 text-orange-600">{card.title}</h3>
                <p className="text-gray-300 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 - Our Comprehensive SEO Services */}
      <section className="bg-gray-900 text-white py-20 px-6 relative overflow-hidden">
        {/* Subtle background graphic */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/black-linen.png')] opacity-10 pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-gray-950 to-transparent opacity-50"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-orange-500">
            Our Comprehensive <span className="text-orange-500">SEO Services</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaSearch size={30} className="text-orange-400" />,
                title: "Keyword Research & Strategy",
                description: "Identifying high-impact keywords that your target audience is searching for to drive relevant traffic.",
              },
              {
                icon: <FaCode size={30} className="text-white" />,
                title: "On-Page SEO Optimization",
                description: "Optimizing website content, meta tags, headers, and internal linking for better search engine visibility.",
              },
              {
                icon: <FaLink size={30} className="text-orange-400" />,
                title: "Off-Page SEO (Link Building)",
                description: "Building high-quality backlinks from authoritative websites to boost your site's domain authority and search rankings.",
              },
              {
                icon: <FaPencilAlt size={30} className="text-white" />,
                title: "Content Marketing & SEO",
                description: "Creating engaging, valuable, and SEO-optimized content that attracts and retains your target audience.",
              },
              {
                icon: <FaMapMarkedAlt size={30} className="text-orange-400" />,
                title: "Local SEO Services",
                description: "Optimizing your online presence for local search results, driving more foot traffic and local inquiries.",
              },
              {
                icon: <FaChartLine size={30} className="text-white" />,
                title: "SEO Audits & Analytics",
                description: "Comprehensive audits to identify SEO opportunities and regular reporting to track performance and ROI.",
              },
            ].map((service, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="bg-gray-800 p-6 rounded-lg shadow-md flex items-start space-x-4 hover:shadow-xl transition-shadow duration-300 border border-gray-700 hover:border-orange-500"
              >
                <div className="flex-shrink-0">{service.icon}</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-orange-600">{service.title}</h3>
                  <p className="text-gray-300 text-base">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      

      {/* SECTION 5 - Why Choose Nadezhda Venture for SEO */}
      <section className="bg-white text-white py-20 px-6 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/dark-circles.png')] opacity-10 pointer-events-none"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-500">
            Why Choose Nadezhda Venture for Your <span className="text-orange-500">SEO Needs</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: <FaUsers size={50} className="text-orange-400 mb-4 mx-auto" />,
                title: "Experienced SEO Specialists",
                description: "Our team comprises certified SEO experts with a proven track record of delivering top rankings and significant traffic growth.",
              },
              {
                icon: <FaLightbulb size={50} className="text-white mb-4 mx-auto" />,
                title: "Data-Driven Strategies",
                description: "We base our strategies on in-depth analytics and market research, ensuring every decision is optimized for results.",
              },
              {
                icon: <FaChartLine size={50} className="text-orange-400 mb-4 mx-auto" />,
                title: "Transparent Reporting",
                description: "Get clear, actionable insights with our regular performance reports, showing the real impact of our SEO efforts.",
              },
            ].map((reason, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="bg-blue-800 p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-700"
              >
                {reason.icon}
                <h3 className="text-xl font-semibold mb-3 mt-2 text-orange-500">{reason.title}</h3>
                <p className="text-white leading-relaxed">{reason.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 - Client Testimonials */}
      {/* <section className="bg-gray-800 text-white py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-10 pointer-events-none"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-500">
            What Our Clients Say About Our <span className="text-orange-500">SEO Services</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              {
                quote: "Nadezhda Venture's SEO strategies completely transformed our online presence. Our organic traffic has skyrocketed, and we're consistently ranking for our target keywords. Highly recommend!",
                author: "Priya Sharma",
                company: "E-commerce Business Owner",
              },
              {
                quote: "Their team's expertise in local SEO helped us dominate our market in Noida. We've seen a significant increase in local leads and customer footfall. Truly impressed!",
                author: "Ankit Singh",
                company: "Restaurant Chain Manager",
              },
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.2 }}
                className="bg-gray-900/70 backdrop-blur-sm p-8 rounded-lg shadow-xl text-left border-l-4 border-orange-500 hover:shadow-2xl transition-shadow duration-300"
              >
                <p className="text-lg italic mb-6 text-gray-100">"{testimonial.quote}"</p>
                <p className="font-semibold text-orange-400">{testimonial.author}</p>
                <p className="text-gray-300 text-sm">{testimonial.company}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* SECTION 7 - Frequently Asked Questions (FAQ) */}
      <section className="bg-white text-white py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/dot-grid.png')] opacity-10 pointer-events-none"></div>

        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-orange-500">
            Common Questions About <span className="text-orange-500">SEO Services</span>
          </h2>
          <div className="space-y-6">
            {[
              {
                question: "What is SEO and why is it important for my business?",
                answer: "SEO, or Search Engine Optimization, is the process of improving your website's visibility on search engines like Google. It's crucial because it drives organic (unpaid) traffic to your site, leading to more leads, sales, and brand recognition.",
              },
              {
                question: "How long does it take to see SEO results?",
                answer: "SEO is a long-term strategy. While some initial improvements can be seen within a few weeks, significant results, like top rankings for competitive keywords, typically take 4-6 months or longer. Consistency and patience are key.",
              },
              {
                question: "Do you offer local SEO services in Noida?",
                answer: "Yes, we specialize in local SEO for businesses in Noida and surrounding areas. Our strategies focus on optimizing your Google My Business profile, local citations, and geo-targeted content to attract local customers.",
              },
              {
                question: "How do you measure the success of your SEO campaigns?",
                answer: "We track various key performance indicators (KPIs) including keyword rankings, organic traffic, conversion rates (e.g., leads, sales), bounce rate, and time on site. We provide detailed monthly reports to show your campaign's progress and ROI.",
              },
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="bg-gray-800/70 backdrop-blur-sm p-6 rounded-lg shadow-md border border-gray-700"
              >
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer text-lg font-semibold text-white">
                    {faq.question}
                    <FaQuestionCircle className="text-orange-400 group-open:rotate-180 transition-transform duration-300" />
                  </summary>
                  <p className="mt-4 text-gray-300 leading-relaxed">{faq.answer}</p>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

     

     
    </div>
  );
};

export default SearchEngineOptimizationPage;